import { SearchIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/web/lib/styling";

interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onChange, debounceMs = 250, className, ...props }, ref) => {
    const [localValue, setLocalValue] = React.useState(value);

    // Sync local state when external value changes
    React.useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // Debounce sync to parent
    React.useEffect(() => {
      const timeout = setTimeout(() => {
        if (localValue !== value) {
          onChange(localValue);
        }
      }, debounceMs);
      return () => clearTimeout(timeout);
    }, [localValue, onChange, value, debounceMs]);

    return (
      <div
        className={cn(
          "inline-flex h-11 min-w-[240px] shrink-0 items-center gap-[10px] rounded-none border border-border",
          "bg-white px-[14px] py-3",
        )}
      >
        <SearchIcon className="size-4 shrink-0 text-withus-dark-blue" />
        <input
          ref={ref}
          type="text"
          placeholder="Search"
          className={cn(
            "h-full flex-1 bg-transparent text-[16px] font-medium text-withus-dark-blue outline-none placeholder:text-withus-dark-blue/30",
            className,
          )}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          {...props}
        />
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";
